import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import { AxiosError } from "axios"
import type { UserDetail } from "@/types/models"
import * as authApi from "@/api/auth"

export type AuthError = "unauthenticated" | "network" | null

interface AuthState {
  user: UserDetail | null
  isAuthenticated: boolean
  isLoading: boolean
  twoFactorToken: string | null
  twoFactorSetupRequired: boolean
  authError: AuthError

  fetchUser: () => Promise<void>
  setUser: (user: UserDetail | null) => void
  setTwoFactorToken: (token: string | null, setupRequired?: boolean) => void
  clearTwoFactor: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      twoFactorToken: null,
      twoFactorSetupRequired: false,
      authError: null,

      fetchUser: async () => {
        try {
          set({ isLoading: true })
          const response = await authApi.getMe()
          // The endpoint now returns 200 with { data: null } when no session is
          // present, instead of 401. We must explicitly handle the null case.
          if (response.data) {
            set({ user: response.data, isAuthenticated: true, authError: null })
          } else {
            set({ user: null, isAuthenticated: false, authError: "unauthenticated" })
          }
        } catch (error) {
          const status = error instanceof AxiosError ? error.response?.status : undefined
          if (status === 401) {
            set({ user: null, isAuthenticated: false, authError: "unauthenticated" })
          } else {
            // Network error / 5xx / timeout — don't flip isAuthenticated, just surface the error
            set({ authError: "network" })
          }
        } finally {
          set({ isLoading: false })
        }
      },

      setUser: (user) => set({ user, isAuthenticated: !!user, isLoading: false, authError: user ? null : "unauthenticated" }),
      setTwoFactorToken: (token, setupRequired = false) => set({ twoFactorToken: token, twoFactorSetupRequired: setupRequired }),
      clearTwoFactor: () => {
        set({ twoFactorToken: null, twoFactorSetupRequired: false })
        void useAuthStore.persist.clearStorage()
      },
    }),
    {
      name: "auth-2fa",
      storage: createJSONStorage(() => sessionStorage),
      // Only persist 2FA-related fields. Never persist user (PII) — it is
      // refetched via /api/auth/me on each load.
      partialize: (state) => ({
        twoFactorToken: state.twoFactorToken,
        twoFactorSetupRequired: state.twoFactorSetupRequired,
      }),
    },
  ),
)
